import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Loader2,
  Share2,
  Sparkles,
  UserX,
} from 'lucide-react';
import ArticleReader from './ArticleReader';
import { SkeletonCard } from './Skeleton';
import EmptyState from './EmptyState';
import './DiscoverFeed.css';

// ── Types ────────────────────────────────────────────────────────────

type DiscoverThread = {
  id: number;
  title: string;
  questionCount: number;
  visibility: 'public' | 'anonymous';
  authorEmail: string | null;
  authorDisplayName: string | null;
  authorAvatarUrl: string | null;
  publishedAt: string;
  hasReport: boolean;
  snippet: string | null;
  reactions: Record<string, number>;
  tags: string[];
};

type SearchResultThread = DiscoverThread & {
  score: number;
  reason: string;
};

type TagCount = { tag: string; count: number };

// ── Constants ────────────────────────────────────────────────────────

/** Hash thread ID → one of 8 gradient palettes for card backgrounds */
const CARD_GRADIENTS = [
  'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  'linear-gradient(135deg, #1b1b2f 0%, #162447 50%, #1f4068 100%)',
  'linear-gradient(135deg, #2d132c 0%, #1a1a2e 50%, #16213e 100%)',
  'linear-gradient(135deg, #0a0a23 0%, #1a1a3e 50%, #2a1a5e 100%)',
  'linear-gradient(135deg, #1a2a1a 0%, #162e23 50%, #0f4038 100%)',
  'linear-gradient(135deg, #2a1a1a 0%, #2e1623 50%, #400f2e 100%)',
  'linear-gradient(135deg, #1a2a2a 0%, #16232e 50%, #0f3460 100%)',
  'linear-gradient(135deg, #2a2a1a 0%, #2e2316 50%, #40300f 100%)',
];

function cardGradient(id: number): string {
  return CARD_GRADIENTS[id % CARD_GRADIENTS.length];
}

// ── Helpers ──────────────────────────────────────────────────────────

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function stripMd(text: string | null): string {
  if (!text) return '';
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`~>-]/g, '')
    .replace(/\n+/g, ' ')
    .trim();
}

function authorInitials(t: DiscoverThread): string {
  const name = t.authorDisplayName || t.authorEmail || '';
  if (!name) return '?';
  return name
    .split(/[\s@]+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ── DiscoverCard ─────────────────────────────────────────────────────

function DiscoverCard({
  thread,
  onClick,
}: {
  thread: DiscoverThread;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const snippet = stripMd(thread.snippet);

  return (
    <button
      className="discover__card"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Background gradient band */}
      <div
        className="discover__card-band"
        style={{ background: cardGradient(thread.id) }}
      />

      {/* Always-visible gradient + content */}
      <div className="discover__card-gradient" />
      <div className="discover__card-content">
        <span className="discover__card-title">{thread.title}</span>
        <span className="discover__card-meta">
          {thread.visibility === 'public' ? (
            <>
              {thread.authorAvatarUrl ? (
                <img
                  className="discover__card-avatar"
                  src={thread.authorAvatarUrl}
                  alt=""
                />
              ) : (
                <span className="discover__card-avatar-fallback">
                  {authorInitials(thread)}
                </span>
              )}
              <span className="discover__card-author">
                {thread.authorDisplayName || thread.authorEmail}
              </span>
            </>
          ) : (
            <>
              <span className="discover__card-avatar-fallback">
                <UserX size={10} />
              </span>
              <span className="discover__card-author">anonymous</span>
            </>
          )}

          {typeof navigator !== 'undefined' && !!navigator.share && (
            <span
              role="button"
              tabIndex={0}
              className="discover__card-share"
              aria-label="Share"
              onClickCapture={(e) => {
                e.stopPropagation();
                navigator.share({
                  title: thread.title,
                  text: `${thread.title} — research via Craft The Future`,
                  url: window.location.href,
                }).catch(() => {});
              }}
            >
              <Share2 size={10} />
            </span>
          )}
        </span>
      </div>

      {/* Hover overlay with snippet + tags */}
      <AnimatePresence>
        {hovered && (snippet || (thread.tags?.length ?? 0) > 0) && (
          <motion.div
            className="discover__card-hover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <span className="discover__card-hover-title">{thread.title}</span>
            {snippet && (
              <span className="discover__card-hover-snippet">{snippet}</span>
            )}
            {thread.tags?.length > 0 && (
              <span className="discover__card-hover-tags">
                {thread.tags.map((tag) => (
                  <span key={tag} className="discover__card-hover-tag">
                    #{tag}
                  </span>
                ))}
              </span>
            )}
            <span className="discover__card-meta" style={{ marginTop: 'auto' }}>
              <span className="discover__card-author" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {relativeTime(thread.publishedAt)}
                {thread.questionCount > 1 && ` \u00b7 ${thread.questionCount} questions`}
              </span>
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
}

// ── SwimLane ──────────────────────────────────────────────────────────

function SwimLane({
  title,
  threads,
  loading,
  onCardClick,
}: {
  title: string;
  threads: DiscoverThread[];
  loading?: boolean;
  onCardClick: (id: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  if (loading) {
    return (
      <div className="discover__lane">
        <h3 className="discover__lane-title">{title}</h3>
        <div className="discover__lane-scroll">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (threads.length === 0) return null;

  return (
    <div className="discover__lane">
      <h3 className="discover__lane-title">{title}</h3>
      <div className="discover__lane-scroll" ref={scrollRef}>
        {threads.map((t) => (
          <DiscoverCard
            key={t.id}
            thread={t}
            onClick={() => onCardClick(t.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────

type Props = {
  onStartThread: (sourceTitle: string, sourceThreadId: number) => void;
  onAskResearch: (threadId: number, title: string) => void;
  searchQuery: string;
  onSearchingChange: (v: boolean) => void;
};

export default function DiscoverFeed({ onStartThread, onAskResearch, searchQuery, onSearchingChange }: Props) {
  // Lane data
  const [trending, setTrending] = useState<DiscoverThread[]>([]);
  const [newest, setNewest] = useState<DiscoverThread[]>([]);
  const [deepDives, setDeepDives] = useState<DiscoverThread[]>([]);
  const [tagLanes, setTagLanes] = useState<
    Array<{ tag: string; threads: DiscoverThread[] }>
  >([]);
  const [lanesLoading, setLanesLoading] = useState(true);

  // Search (query is lifted to parent; results managed locally)
  const [searchResults, setSearchResults] = useState<SearchResultThread[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const searchAbortRef = useRef<AbortController | null>(null);

  // Article reader
  const [openThreadId, setOpenThreadId] = useState<number | null>(null);

  // ── Load all swim lanes on mount ─────────────────────────────────
  const loadLanes = useCallback(async () => {
    setLanesLoading(true);

    const [trendingRes, newestRes, deepRes, tagsRes] = await Promise.allSettled([
      fetch('/api/discover?sort=trending', { credentials: 'include' }).then(
        (r) => r.json() as Promise<{ ok?: boolean; threads?: DiscoverThread[] }>
      ),
      fetch('/api/discover?sort=newest', { credentials: 'include' }).then(
        (r) => r.json() as Promise<{ ok?: boolean; threads?: DiscoverThread[] }>
      ),
      fetch('/api/discover?sort=deep-dives', { credentials: 'include' }).then(
        (r) => r.json() as Promise<{ ok?: boolean; threads?: DiscoverThread[] }>
      ),
      fetch('/api/discover/tags', { credentials: 'include' }).then(
        (r) => r.json() as Promise<{ ok?: boolean; tags?: TagCount[] }>
      ),
    ]);

    if (trendingRes.status === 'fulfilled' && trendingRes.value.ok) {
      setTrending(trendingRes.value.threads ?? []);
    }
    if (newestRes.status === 'fulfilled' && newestRes.value.ok) {
      setNewest(newestRes.value.threads ?? []);
    }
    if (deepRes.status === 'fulfilled' && deepRes.value.ok) {
      setDeepDives(deepRes.value.threads ?? []);
    }

    // Load per-tag lanes for top 5 tags
    if (tagsRes.status === 'fulfilled' && tagsRes.value.ok) {
      const topTags = (tagsRes.value.tags ?? []).slice(0, 5);
      const tagResults = await Promise.allSettled(
        topTags.map((t) =>
          fetch(`/api/discover?tag=${encodeURIComponent(t.tag)}`, {
            credentials: 'include',
          }).then(
            (r) =>
              r.json() as Promise<{ ok?: boolean; threads?: DiscoverThread[] }>
          )
        )
      );

      const lanes: Array<{ tag: string; threads: DiscoverThread[] }> = [];
      topTags.forEach((t, i) => {
        const result = tagResults[i];
        if (
          result.status === 'fulfilled' &&
          result.value.ok &&
          (result.value.threads ?? []).length > 0
        ) {
          lanes.push({ tag: t.tag, threads: result.value.threads! });
        }
      });
      setTagLanes(lanes);
    }

    setLanesLoading(false);
  }, []);

  useEffect(() => {
    void loadLanes();
  }, [loadLanes]);

  // ── Debounced AI search ──────────────────────────────────────────
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setIsSearchMode(false);
      setSearchResults(null);
      return;
    }

    setIsSearchMode(true);

    const timeout = setTimeout(async () => {
      // Abort any in-flight search
      searchAbortRef.current?.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;

      setSearching(true);
      onSearchingChange(true);
      try {
        const res = await fetch('/api/discover/search', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: trimmed }),
          signal: controller.signal,
        });
        const data = (await res.json()) as {
          ok?: boolean;
          results?: SearchResultThread[];
          error?: string;
        };
        if (!controller.signal.aborted) {
          if (data.ok) {
            setSearchResults(data.results ?? []);
          } else {
            setSearchResults([]);
          }
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          setSearchResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearching(false);
          onSearchingChange(false);
        }
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // ── Check if all lanes are empty ────────────────────────────────
  const allEmpty =
    !lanesLoading &&
    trending.length === 0 &&
    newest.length === 0 &&
    deepDives.length === 0 &&
    tagLanes.length === 0;

  return (
    <div className="discover">
      {/* ── Content: search results OR swim lanes ── */}
      <AnimatePresence mode="wait">
        {isSearchMode ? (
          <motion.div
            key="search"
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {searching && (
              <div className="discover__state">
                <Sparkles size={14} /> searching with AI...
              </div>
            )}

            {!searching && searchResults && searchResults.length === 0 && (
              <EmptyState
                title="no matches found."
                subtitle="try rephrasing your query or using different terms."
              />
            )}

            {!searching && searchResults && searchResults.length > 0 && (
              <>
                <div className="discover__search-header">
                  <h3>Results</h3>
                  <span className="discover__search-count">
                    {searchResults.length} match
                    {searchResults.length !== 1 ? 'es' : ''}
                  </span>
                </div>
                <div className="discover__search-results">
                  {searchResults.map((t) => (
                    <div key={t.id}>
                      <DiscoverCard
                        thread={t}
                        onClick={() => setOpenThreadId(t.id)}
                      />
                      {t.reason && (
                        <p className="discover__search-reason">{t.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="lanes"
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {lanesLoading && (
              <div className="discover__state">
                <Loader2 size={14} className="discover__spin" /> loading
                discover feed...
              </div>
            )}

            {allEmpty && (
              <EmptyState
                title="no published threads yet."
                subtitle="when someone publishes a public or anonymous thread, it shows up here."
              />
            )}

            <SwimLane
              title="Trending now"
              threads={trending}
              onCardClick={setOpenThreadId}
            />

            <SwimLane
              title="New from the community"
              threads={newest}
              onCardClick={setOpenThreadId}
            />

            {tagLanes.map((lane) => (
              <SwimLane
                key={lane.tag}
                title={`#${lane.tag}`}
                threads={lane.threads}
                onCardClick={setOpenThreadId}
              />
            ))}

            <SwimLane
              title="Deep dives"
              threads={deepDives}
              onCardClick={setOpenThreadId}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Article reader modal ────────────────────────────────── */}
      <ArticleReader
        threadId={openThreadId}
        onClose={() => setOpenThreadId(null)}
        onStartThread={onStartThread}
        onAskResearch={(id, title) => {
          setOpenThreadId(null);
          onAskResearch(id, title);
        }}
        onOpenThread={setOpenThreadId}
      />
    </div>
  );
}
